import { Component, ElementRef, HostListener, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TablerIconComponent } from 'angular-tabler-icons';
import { TooltipModule } from 'primeng/tooltip';

import { LayoutService } from 'src/app/shared/services/layout.service';
import { UserProfile, Tenant } from 'src/app/shared/user/models/user';
import { UserStore } from 'src/app/shared/user/store/user.store';

/** DS TenantSelect/TenantBadge/TenantMenu (Soll: navigation.card.html im
    claude.ai/design-Projekt): expanded = Button + weißes Menü mit orangenem
    Häkchen; compact = klickbarer Initialen-Badge mit demselben Menü als
    Popover rechts. Bei nur einem Mandanten: statische Anzeige. */
@Component({
  selector: 'app-active-tenant-menu',
  standalone: true,
  imports: [CommonModule, TablerIconComponent, TooltipModule],
  templateUrl: './active-tenant-menu.component.html',
  styleUrl: './active-tenant-menu.component.css',
})
export class ActiveTenantMenuComponent {
  userStore = inject(UserStore);
  layoutService = inject(LayoutService);
  private readonly el = inject(ElementRef);

  protected readonly menuOpen = signal(false);

  userTenants = computed<Tenant[]>(() =>
    this.getTenantsFromUser(this.userStore.userProfile() as UserProfile),
  );
  // Ohne Mandanten (noch in keinen eingeladen) gibt es nichts zu wechseln -> Switch ausblenden.
  showTenantSwitch = computed<boolean>(() => this.userTenants().length > 0);
  activeTenant = this.userStore.activeTenant;
  switchable = computed(() => (this.userTenants()?.length ?? 0) > 1);
  tenantShort = computed(() => {
    const name = this.activeTenant()?.tenantDescription ?? '';
    const parts = name.trim().split(/\s+/).filter(Boolean);
    const letters = parts.slice(0, 2).map((p) => p[0]?.toUpperCase() ?? '');
    return letters.join('') || name.slice(0, 2).toUpperCase();
  });

  toggleMenu(): void {
    if (!this.switchable()) return;
    this.menuOpen.update((o) => !o);
  }

  @HostListener('document:mousedown', ['$event'])
  onDocumentMouseDown(event: MouseEvent): void {
    if (this.menuOpen() && !this.el.nativeElement.contains(event.target)) {
      this.menuOpen.set(false);
    }
  }

  isActive(tenant: Tenant): boolean {
    return tenant.tenantId === this.activeTenant()?.tenantId;
  }

  selectTenant(tenant: Tenant): void {
    // loadNectaModules wird zentral in app.component via effect auf activeTenant getriggert.
    this.userStore.setActiveNectaTenant(tenant);
    this.menuOpen.set(false);
  }

  // Extrahiert die eindeutigen Tenants aus dem UserProfile.
  private getTenantsFromUser(userProfile: UserProfile): Tenant[] {
    let tenants: Tenant[] = [];
    userProfile?.nectaUsers?.forEach((u) => {
      if (!tenants.some((x) => u.tenantId == x.tenantId))
        tenants.push({
          tenantId: u.tenantId,
          tenantDescription: u.tenantDescription,
          costCenterId: u.costCenterId,
        } as Tenant);
    });
    return tenants;
  }
}
